import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../../routes/routeConstants";
import { SUBJECT_CATEGORIES, SUBJECTS } from "../../../data/academicTaxonomy";
import { RegisterData } from "../../sections/desk/useRegister";
import { Leaf, P } from "./shell";
import { VELLUM as T, SANS, MONO, mono, body, SheetHead, TitleBlock } from "./tokens";

/* A-05 · schedule of fields.

   The taxonomy is real and complete from day one, so this leaf reads well at
   any size of register: a field with no posts still holds its place, and the
   schedule visibly fills as entries arrive. */

const COLS = "minmax(0,1fr) 86px 66px";

export const LeafIndex: React.FC<{ register: RegisterData }> = ({ register }) => {
  const subjectsPerField = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of SUBJECTS) m.set(s.category, (m.get(s.category) || 0) + 1);
    return m;
  }, []);

  const postsPerField = useMemo(() => {
    const m = new Map<string, number>();
    for (const j of register.jobs) {
      if (!j.subjectCategory) continue;
      m.set(j.subjectCategory, (m.get(j.subjectCategory) || 0) + 1);
    }
    return m;
  }, [register.jobs]);

  return (
    <Leaf
      code="§ A-05 · schedule of fields"
      measure={
        <>
          <SheetHead t={T} code={`${SUBJECTS.length} subjects`}>
            Every field, already mapped.
          </SheetHead>

          <p style={{ ...body(T), fontSize: 16.5, maxWidth: 600, marginBottom: 28 }}>
            The register is filed against the full academic subject taxonomy - {SUBJECTS.length}{" "}
            subjects across {SUBJECT_CATEGORIES.length} fields - and has been since the day it
            opened. A field with no posts today still holds its place in the index.
          </p>

          <div
            style={{
              ...mono(T, T.ink),
              display: "grid",
              gridTemplateColumns: COLS,
              gap: 12,
              borderBottom: `1.5px solid ${T.ink}`,
              paddingBottom: 9,
            }}
          >
            <span>Field</span>
            <span style={{ textAlign: "right" }}>Subjects</span>
            <span style={{ textAlign: "right" }}>Posts</span>
          </div>

          <div>
            {SUBJECT_CATEGORIES.map((field) => {
              const subjects = subjectsPerField.get(field) || 0;
              const posts = postsPerField.get(field) || 0;
              const live = posts > 0;

              const inner = (
                <>
                  <span style={{ fontFamily: SANS, fontSize: 15.5, color: live ? T.ink : T.mute, minWidth: 0 }}>
                    {field}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 13, color: T.mute, textAlign: "right" }}>{subjects}</span>
                  <span style={{ fontFamily: MONO, fontSize: 13, color: live ? T.accent : T.mute, textAlign: "right" }}>
                    {live ? posts : "·"}
                  </span>
                </>
              );

              const rowStyle: React.CSSProperties = {
                display: "grid",
                gridTemplateColumns: COLS,
                gap: 12,
                alignItems: "baseline",
                padding: "11px 0",
                borderBottom: `1px solid ${T.rule}`,
                textDecoration: "none",
              };

              /* Only a field that actually holds posts is a door. An empty
                 field stays legible but promises nothing. */
              return live ? (
                <Link
                  key={field}
                  to={`${ROUTE_NAMES.PAGES.JOBS}?q=${encodeURIComponent(field)}`}
                  style={rowStyle}
                  className={`${P}-index-row`}
                >
                  {inner}
                </Link>
              ) : (
                <div key={field} style={rowStyle}>
                  {inner}
                </div>
              );
            })}
          </div>
        </>
      }
      margin={
        <>
          <TitleBlock
            t={T}
            rows={[
              ["Fields", String(SUBJECT_CATEGORIES.length)],
              ["Subjects", String(SUBJECTS.length)],
              ["Fields in use", register.loading ? "·" : String(register.fieldsCovered)],
            ]}
          />
          <p style={{ ...body(T), fontSize: 13.5, marginTop: 16 }}>
            The same taxonomy files the posts and the profiles, so a scholar and a position are
            described in one vocabulary rather than two.
          </p>
        </>
      }
    />
  );
};
